import dayjs from 'dayjs'
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'

import { ICoordinates } from 'modules/HomesForSale/types'

import * as T from 'types'

import GoogleMap from './GoogleMap'

interface IProps {
  initialCenter: {
    latitude: number
    longitude: number
  }
  listings: T.IListing[]
  zoom: number
  onMapBoundsChange: (bounds: [number, number][], center: ICoordinates) => void
  onZoomChange: (zoom: number) => void
  setHighlightedListingId: (listingId: string) => void
}

/**
 * This map component uses the following open source solution as a base:
 * https://dawchihliou.github.io/articles/building-custom-google-maps-marker-react-component-like-airbnb-in-nextjs
 *
 * We are eternally grateful to Daw-Chih Liou!
 */
const MapSearch: FunctionComponent<IProps> = ({
  initialCenter,
  listings,
  zoom,
  onMapBoundsChange,
  onZoomChange,
  setHighlightedListingId,
}) => {
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: initialCenter.latitude,
    lng: initialCenter.longitude,
  })

  useEffect(() => {
    setCenter({ lat: initialCenter.latitude, lng: initialCenter.longitude })
  }, [initialCenter.latitude, initialCenter.longitude])

  const onIdle = (map: google.maps.Map) => {
    const mapZoom = map.getZoom()
    if (mapZoom && onZoomChange) onZoomChange(mapZoom)

    const nextCenter = map.getCenter()

    if (nextCenter) setCenter(nextCenter.toJSON())
  }

  const [highlightedListing, setHighlightedListing] = useState<T.IListing | null>(null)

  const onMarkerClick = useCallback(
    (payload: T.IListing) => {
      setHighlightedListing(payload)
      setHighlightedListingId(payload.customLinkUrl)
    },
    [highlightedListing],
  )

  const [hoverActiveListing, setHoverActiveListing] = useState<T.IListing | null>(null)
  const onMarkerHover = (payload: T.IListing) => setHoverActiveListing(payload)

  const onBoundsChanged = (map: google.maps.Map) => {
    console.warn('onBoundsChanged ', dayjs().format('HH:mm:ss.SSS'))

    const newCenter = map.getCenter()

    const newCenterCoordinates: ICoordinates = {
      latitude: newCenter?.lat() as number,
      longitude: newCenter?.lng() as number,
    }

    const bounds = map.getBounds()
    const northEastBound = bounds?.getNorthEast()
    const southWestBound = bounds?.getSouthWest()

    const northEastCoordinates = {
      latitude: northEastBound?.lat(),
      longitude: northEastBound?.lng(),
    }

    const southWestCoordinates = {
      latitude: southWestBound?.lat(),
      longitude: southWestBound?.lng(),
    }

    if (
      northEastCoordinates.latitude
      && northEastCoordinates.longitude
      && southWestCoordinates.latitude
      && southWestCoordinates.longitude
    ) {
      // we're taking the two corners of the map google gives us and deriving the other two corners
      // so that we have a complete polygon (rectangle) to send to the backend to make a geo
      // query. Note
      //  - the order of the coordinates is important (it must draw a rectangle)
      //  - the coordinates are in the order [longitude, latitude], per GeoJSON spec
      onMapBoundsChange(
        [
          // north east
          [northEastCoordinates.longitude, northEastCoordinates.latitude],
          // north west
          [southWestCoordinates.longitude, northEastCoordinates.latitude],
          // south west
          [southWestCoordinates.longitude, southWestCoordinates.latitude],
          // south east
          [northEastCoordinates.longitude, southWestCoordinates.latitude],
        ],
        newCenterCoordinates,
      )
    }
  }

  const listingsWithCoordinates = useMemo(
    () => listings?.filter(listing => listing.location.coordinates.every(Boolean)),
    [listings],
  )

  return (
    <GoogleMap
      apiKey={process.env.GOOGLE_API_KEY || ''}
      center={center}
      zoom={zoom}
      markers={listingsWithCoordinates}
      onBoundsChanged={onBoundsChanged}
      onIdle={onIdle}
      onMarkerClick={onMarkerClick}
      onMarkerHover={onMarkerHover}
      onZoomChanged={onZoomChange}
      hoveredMarkerId={hoverActiveListing?._id}
    />
  )
}

export default MapSearch
